import { useState } from "react";
import { VINTED_CONDITIONS, PACKAGES } from "@/pages/vinted/VintedFields";
import { MERCARI_CONDITIONS } from "@/pages/vinted/mercariOptions";

export const EMPTY_MARKET = { vinted: {}, mercari: {} };
const clean = (o) => Object.fromEntries(Object.entries(o || {}).filter(([, v]) => (v || "").trim()));
// Only what the seller picked; blank = SnapList works it out (or the batch's choice).
export const marketPayload = (m) => {
  const v = clean(m?.vinted); const me = clean(m?.mercari);
  return { ...(Object.keys(v).length ? { vinted: v } : {}), ...(Object.keys(me).length ? { mercari: me } : {}) };
};
const PAYER = { seller: "Free shipping", buyer: "Buyer pays" };
const METHOD = { own: "Ship on your own", label: "Mercari label" };
export const marketSummary = (it) => {
  const v = it.vinted || {}; const m = it.mercari || {};
  const parts = [v.condition, v.color, v.size, v.package && `${v.package} box`].filter(Boolean);
  const mp = [m.condition, PAYER[m.payer], METHOD[m.method], m.box_fit && `fits 14x10x5: ${m.box_fit}`].filter(Boolean);
  return [parts.length && `Vinted · ${parts.join(", ")}`, mp.length && `Mercari · ${mp.join(", ")}`].filter(Boolean).join(" · ");
};

const sel = "h-8 w-full rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-800";
const lab = "text-[10px] font-mono uppercase tracking-wider text-slate-500";
const Chips = ({ value, options, onChange, testid, color }) => (
  <div className="mt-1 flex flex-wrap gap-1" data-testid={testid}>
    {options.map(([k, t]) => (
      <button key={k || "auto"} type="button" onClick={() => onChange(k)} aria-pressed={(value || "") === k} data-testid={`${testid}-${k || "auto"}`}
        style={(value || "") === k ? { background: color, borderColor: color } : undefined}
        className={`h-7 rounded-full border px-2.5 text-[11px] font-semibold transition-colors ${(value || "") === k ? "text-white" : "border-slate-300 bg-white text-slate-600 hover:border-slate-500"}`}>{t}</button>
    ))}
  </div>
);
const V = "#09B1BA"; const M = "#f26222";

export function MercariShipping({ value, onChange, prefix, inherit = {} }) {
  const set = (k, x) => onChange({ ...value, [k]: x });
  return <>
    <Chips value={value.payer} onChange={(k) => set("payer", k)} options={[["", inherit.payer ? `Batch (${PAYER[inherit.payer]})` : "Auto (free)"], ["seller", "Free shipping"], ["buyer", "Buyer pays"]]} testid={`${prefix}-payer`} color={M} />
    <Chips value={value.method} onChange={(k) => set("method", k)} options={[["", inherit.method ? `Batch (${METHOD[inherit.method]})` : "Auto (own)"], ["own", "Ship on your own"], ["label", "Mercari label"]]} testid={`${prefix}-method`} color={M} />
    <p className={`${lab} mt-1.5`}>Fits in a 14 x 10 x 5 in box?</p>
    <Chips value={value.box_fit} onChange={(k) => set("box_fit", k)} options={[["yes", "Yes"], ["no", "No"], ["", inherit.box_fit ? `Batch (${inherit.box_fit === "yes" ? "Yes" : "No"})` : "Skip"]]} testid={`${prefix}-box`} color={M} />
  </>;
}

// Vinted + Mercari picks for one item (bulk upload or single listing). Everything is optional; `inherit` = the batch's picks.
export default function MarketItemFields({ value, onChange, testid = "market", inherit }) {
  const [open, setOpen] = useState(!!marketSummary(value || EMPTY_MARKET));
  const v = value?.vinted || {}; const m = value?.mercari || {};
  const setV = (k, x) => onChange({ ...value, vinted: { ...v, [k]: x } });
  const setM = (next) => onChange({ ...value, mercari: next });
  if (!open) {
    return <button type="button" onClick={() => setOpen(true)} data-testid={`${testid}-toggle`}
      className="mt-2 h-7 rounded-full border border-dashed border-slate-300 px-3 text-[11px] font-semibold text-slate-600 hover:border-slate-500">+ For Vinted / Mercari: condition, shipping, box</button>;
  }
  return (
    <div className="mt-2 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-2.5" data-testid={`${testid}-fields`}>
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "#067A80" }}>For Vinted</p>
        <div className="mt-1 grid grid-cols-3 gap-1.5">
          <select value={v.condition || ""} onChange={(e) => setV("condition", e.target.value)} className={`${sel} col-span-3`} aria-label="Vinted condition" data-testid={`${testid}-vinted-condition`}>
            <option value="">Condition: auto from photos</option>
            {VINTED_CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input value={v.color || ""} onChange={(e) => setV("color", e.target.value.slice(0, 40))} placeholder="Color (auto)" className={sel} data-testid={`${testid}-vinted-color`} />
          <input value={v.size || ""} onChange={(e) => setV("size", e.target.value.slice(0, 40))} placeholder="Size (auto)" className={`${sel} col-span-2`} data-testid={`${testid}-vinted-size`} />
        </div>
        <Chips value={v.package} onChange={(k) => setV("package", k)} options={[["", inherit?.vinted_package ? `Batch (${inherit.vinted_package})` : "Size: auto"], ...PACKAGES]} testid={`${testid}-vinted-package`} color={V} />
      </div>
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "#c2410c" }}>For Mercari</p>
        <select value={m.condition || ""} onChange={(e) => setM({ ...m, condition: e.target.value })} className={`${sel} mt-1`} aria-label="Mercari condition" data-testid={`${testid}-mercari-condition`}>
          <option value="">Condition: auto from photos</option>
          {MERCARI_CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <MercariShipping value={m} onChange={setM} prefix={`${testid}-mercari`} inherit={inherit?.mercari} />
      </div>
    </div>
  );
}

// Bulk upload: one Vinted shipping size and one set of Mercari shipping picks for the whole batch (items can override).
export function MarketBatchDefaults({ value, onChange }) {
  const [open, setOpen] = useState(!!(value.vinted_package || marketSummary({ mercari: value.mercari })));
  return (
    <div className="mb-3 rounded-lg border border-slate-200 p-2.5" data-testid="batch-market-all">
      <button type="button" onClick={() => setOpen(!open)} className="text-xs font-semibold text-slate-800" data-testid="batch-market-all-toggle">
        {open ? "−" : "+"} Vinted &amp; Mercari for the whole batch
      </button>
      {open && <div className="mt-2 space-y-1.5">
        <p className={lab}>Vinted shipping size</p>
        <Chips value={value.vinted_package} onChange={(k) => onChange({ ...value, vinted_package: k })} options={[["", "Auto"], ...PACKAGES]} testid="batch-market-all-vinted-package" color={V} />
        <p className={`${lab} mt-1`}>Mercari shipping</p>
        <MercariShipping value={value.mercari || {}} onChange={(mercari) => onChange({ ...value, mercari })} prefix="batch-market-all-mercari" />
        <p className="text-[10px] text-slate-500">Every item gets these; pick something different on an item to override it.</p>
      </div>}
    </div>
  );
}
